import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { type CICaseDetail } from 'components/CriticalIncident/interfaces';
import { computeTotalTime } from 'components/CriticalIncident/utils/CaseSummaryUtils';
import { Spinner } from 'components/Shared';
import { LabelValue } from 'components/Shared/LabelValue';
import { CICard } from '../../Shared/CICard/CICard';
import { DateRangeWidget } from '../../DateRangeWidget';
import { IncidentReportStatusChip } from '../CICaseSummaryTabs/IncidentReportTab/IncidentReportStatusChip';
import { useIncidentReportContext } from '../CICaseSummaryTabs/IncidentReportTab/IncidentReportContextProvider';
import { useFeatureManager } from 'hooks/useFeatureManager';
import { useGetAllReferences } from 'api/queries/referencesQuery';

import './CICaseSummarySidePanel.scss';

export const CICaseSummarySidePanel: React.FC<{ ciCase: CICaseDetail, isModal?: boolean }> = ({ ciCase, isModal }) => {
    const { t, ready: translationsReady } = useTranslation('criticalIncident', { useSuspense: false });
    const { isIncidentReportFeatureEnabled } = useFeatureManager();
    const { report: incidentReport, errorSections } = useIncidentReportContext();
    const { data: references, isLoading: referencesLoading } = useGetAllReferences();

    const accountName = [ciCase?.organization?.company, ciCase?.organization?.branch, ciCase?.organization?.division].filter(Boolean).join(', ');

    const sidePanelContent = (
        <>
            {isIncidentReportFeatureEnabled() && (<section>
                <h4>{t('incident_report')}</h4>
                <div><IncidentReportStatusChip status={incidentReport?.status!} hasError={!!errorSections?.length} /></div>
            </section>)}
            <section>
                <h4>{t('account')}</h4>
                <LabelValue label={t('client')} value={accountName} />
                <LabelValue label={t('case_id')} value={ciCase?.externalCaseId} />
                <LabelValue label={t('work_order_id')} value={ciCase?.externalWorkOrderId} />
            </section>
            <section>
                <h4>{t('intervention')}</h4>
                <LabelValue label={t('service')} value={referencesLoading ? undefined : references?.services?.find(s => s.value === ciCase.incident?.serviceCode)?.title} />
                <DateRangeWidget
                    startDate={ciCase.intervention?.startDateTime}
                    endDate={ciCase.intervention?.endDateTime}
                    clientTimezone={ciCase.intervention?.olsonName ?? undefined}
                    inline
                />
                <LabelValue
                    label={t('duration')}
                    className="with-capitalized-value"
                    value={computeTotalTime(t, ciCase.intervention?.startDateTime, ciCase.intervention?.endDateTime)}
                />
                <LabelValue label={t('modality')} value={referencesLoading ? undefined : references?.modalities?.find(m => m.value === ciCase?.intervention?.modalityCode)?.title} />
                <LabelValue
                    label={t('location')}
                    value={ciCase?.intervention?.location}
                />
            </section>
            <section>
                <h4>{t('onsite_contact')}</h4>
                <LabelValue label={t('name')} value={ciCase?.contacts?.onSite?.name ?? undefined} />
                <LabelValue label={t('phone')} value={ciCase?.contacts?.onSite?.phone ?? undefined} />
                <LabelValue label={t('email')} value={ciCase?.contacts?.onSite?.email ?? undefined} />
            </section>
        </>
    );

    return translationsReady ? (
        isModal ? (
            <div style={{ paddingBottom: '10px' }}>
              {sidePanelContent}
            </div>
        ) : (
            <>
                <Container className="ci_summary_side_panel_container" disableGutters={true}>
                    <Paper className="ci_summary_side_panel_div" elevation={0}>
                        <h1>{accountName}</h1>

                        <CICard className="ci_summary_side_panel_card">
                            <h2>{t('summary')}</h2>
                            {sidePanelContent}
                        </CICard>
                    </Paper>
                </Container>
            </>
        )
    ) : (
        <Spinner fillParent />
    );
};
